import React from "react";
import { Box, Button, Checkbox, Typography } from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import FilterSearchInput from "./FilterSearchInput";
import { ratioMatchesWhRatioSearch } from "./sizeUtils";

const INITIAL_LIMIT = 100;

/** Renders "XS 1, S 1, M 2" — alpha faded, qty dark. */
export function WhRatioLabel({ ratioKey, selected = false, fontSize = "0.6rem" }) {
	if (!ratioKey) return null;
	const parts = ratioKey.split(",").map((p) => p.trim()).filter(Boolean);
	return (
		<Box component="span" sx={{ fontSize, lineHeight: 1.35 }}>
			{parts.map((part, i) => {
				const m = part.match(/^(.+?)\s+(\d+)$/);
				if (!m) {
					return (
						<Box component="span" key={i}>
							{i > 0 ? ", " : ""}
							{part}
						</Box>
					);
				}
				return (
					<Box component="span" key={i}>
						{i > 0 ? ", " : ""}
						<Box
							component="span"
							sx={{
								color: selected ? "rgba(255,255,255,0.72)" : "rgba(0,0,0,0.38)",
								fontWeight: 400,
							}}>
							{m[1]}
						</Box>
						<Box
							component="span"
							sx={{
								color: selected ? "#fff" : "#111",
								fontWeight: 700,
								ml: 0.25,
							}}>
							{m[2]}
						</Box>
					</Box>
				);
			})}
		</Box>
	);
}

const SizeRatioFilterDropdown = ({
	options,
	selectedSet,
	isOpen,
	searchTerm,
	onToggle,
	onSearchChangeDebounced,
	onSelectionChange,
}) => {
	const filtered = options.filter(
		({ key }) => !searchTerm.trim() || ratioMatchesWhRatioSearch(key, searchTerm)
	);

	const selectedFirst = [
		...filtered.filter(({ key }) => selectedSet.has(key)),
		...filtered.filter(({ key }) => !selectedSet.has(key)),
	];

	const hasSearch = searchTerm.trim() !== "";
	const optionsToDisplay = hasSearch
		? selectedFirst
		: (() => {
				const first = selectedFirst.slice(0, INITIAL_LIMIT);
				const selectedNotInFirst = selectedFirst.filter(
					({ key }) => selectedSet.has(key) && !first.some((o) => o.key === key)
				);
				return [...first, ...selectedNotInFirst];
			})();

	const isTruncated = !hasSearch && selectedFirst.length > INITIAL_LIMIT;

	const handleCheckboxChange = (key, checked) => {
		const newSet = new Set(selectedSet);
		if (checked) newSet.add(key);
		else newSet.delete(key);
		onSelectionChange(newSet);
	};

	const handleSelectAll = () => {
		const keys = optionsToDisplay.map((o) => o.key);
		const selectedInDisplay = keys.filter((k) => selectedSet.has(k));
		const allSelected = keys.length > 0 && selectedInDisplay.length === keys.length;
		onSelectionChange(allSelected ? new Set() : new Set(keys));
	};

	const getDisplayText = () => {
		if (selectedSet.size === 0) return "Size Ratio (WH)";
		if (selectedSet.size === 1) return Array.from(selectedSet)[0];
		return `${selectedSet.size} ratios selected`;
	};

	return (
		<Box sx={{ position: "relative", width: "100%" }} data-dropdown>
			<Button
				onClick={onToggle}
				variant="outlined"
				fullWidth
				sx={{
					justifyContent: "space-between",
					textTransform: "none",
					fontSize: "0.6875rem",
					height: "40px",
					backgroundColor: "#f8f8f8",
					borderColor: "#000000",
					borderWidth: isOpen ? "2px" : "1px",
					color: selectedSet.size > 0 ? "#000000" : "#666666",
					fontWeight: selectedSet.size > 0 ? 500 : 400,
					"&:hover": {
						borderColor: "#000000",
						borderWidth: "2px",
						backgroundColor: "#f0f0f0",
					},
					"& .MuiButton-endIcon": {
						transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
						transition: "transform 0.2s ease-in-out",
					},
				}}
				endIcon={<ExpandMoreIcon />}>
				<Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left", flex: 1 }}>
					{selectedSet.size === 1 ? (
						<WhRatioLabel ratioKey={Array.from(selectedSet)[0]} fontSize="0.65rem" />
					) : (
						getDisplayText()
					)}
				</Box>
			</Button>

			{isOpen && (
				<Box
					sx={{
						position: "absolute",
						top: "100%",
						left: 0,
						right: 0,
						zIndex: 1300,
						backgroundColor: "#f8f8f8",
						border: "1px solid #666666",
						borderRadius: "4px",
						boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
						mt: 0.5,
						maxHeight: "320px",
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
						minWidth: 280,
					}}>
					<Box sx={{ p: 1, borderBottom: "1px solid #e0e0e0" }}>
						<FilterSearchInput
							field="sizeRatio"
							label="Size Ratio"
							placeholder="e.g. 1,2,2,1 or XS"
							value={searchTerm}
							onSearchChangeDebounced={onSearchChangeDebounced}
							sx={{
								"& .MuiOutlinedInput-root": {
									fontSize: "0.6rem",
									height: "32px",
									backgroundColor: "#ffffff",
								},
							}}
						/>
					</Box>

					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							px: 1,
							py: 0.5,
							borderBottom: "1px solid #666666",
							backgroundColor: "#ffffff",
						}}>
						<Checkbox
							checked={
								optionsToDisplay.length > 0 &&
								optionsToDisplay.every(({ key }) => selectedSet.has(key))
							}
							indeterminate={
								optionsToDisplay.some(({ key }) => selectedSet.has(key)) &&
								!optionsToDisplay.every(({ key }) => selectedSet.has(key))
							}
							onChange={handleSelectAll}
							size="small"
							sx={{ p: 0.5, color: "#666", "&.Mui-checked": { color: "#000" } }}
						/>
						<Typography variant="body2" sx={{ fontSize: "0.6rem", fontWeight: 500, color: "#666", ml: 0.5 }}>
							Select All
						</Typography>
						<Typography variant="caption" sx={{ fontSize: "0.55rem", color: "#999", ml: "auto" }}>
							WH SOH min
						</Typography>
					</Box>

					{isTruncated && (
						<Box sx={{ px: 1, py: 0.5, backgroundColor: "#f5f5f5", borderBottom: "1px solid #e0e0e0" }}>
							<Typography variant="caption" sx={{ fontSize: "0.65rem", color: "#666" }}>
								Showing first {INITIAL_LIMIT} of {selectedFirst.length}. Search to find more.
							</Typography>
						</Box>
					)}

					<Box sx={{ maxHeight: "220px", overflow: "auto" }}>
						{optionsToDisplay.length === 0 ? (
							<Typography variant="body2" sx={{ color: "#666", fontStyle: "italic", textAlign: "center", py: 2, px: 1, fontSize: "0.6rem" }}>
								{searchTerm ? "No matching ratios" : "No WH size ratios in current results"}
							</Typography>
						) : (
							optionsToDisplay.map(({ key, count }) => {
								const checked = selectedSet.has(key);
								return (
									<Box
										key={key}
										sx={{
											display: "flex",
											alignItems: "center",
											py: 0.6,
											px: 1,
											"&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
										}}>
										<Checkbox
											checked={checked}
											onChange={(e) => handleCheckboxChange(key, e.target.checked)}
											size="small"
											sx={{ p: 0.5, color: "#666", "&.Mui-checked": { color: "#000" } }}
										/>
										<Box sx={{ flex: 1, minWidth: 0, ml: 0.5 }}>
											<WhRatioLabel ratioKey={key} selected={false} />
										</Box>
										{count > 0 && (
											<Box
												component="span"
												sx={{
													ml: 0.75,
													bgcolor: checked ? "#3949ab" : "#e8eaf6",
													color: checked ? "#fff" : "#3949ab",
													fontSize: "0.55rem",
													fontWeight: 700,
													borderRadius: 3,
													px: 0.5,
													py: 0.1,
													flexShrink: 0,
												}}>
												{count}
											</Box>
										)}
									</Box>
								);
							})
						)}
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default SizeRatioFilterDropdown;
